import _ from 'lodash'
import translate from 'src/common/helpers/language/translate'
import globalTranslateObject from 'src/translate/global.translate'
import { reactive, toValue, watch } from 'vue'
import translationObject from './translate/baseAvailability.translate'
import {
  IBaseAvailabilityHookArgs,
  IBaseAvailabilityStateReactive,
  IBaseAvailabilityTableRow,
} from './interfaces/baseAvailability.interface'
import { IAvailabilityData, ILocalSalesType } from 'src/common/interfaces'

export default function useBaseAvailabilityHook({ props, model }: IBaseAvailabilityHookArgs) {
  const { t } = translate(translationObject)
  const { t: globalT } = translate(globalTranslateObject)

  /* ---------------------------------- State --------------------------------- */

  const state = reactive<IBaseAvailabilityStateReactive>({
    salesTypeToggle: true,
    tableCols: [],
    tableRows: [],
    allSalesTypes: [],
    salesTypes: props.salesTypes ?? [],
    availabilities: props.availabilities ?? [],
    availabilityValues: model.value.availability,
    availabilityDialog: false,
    availabilityDetails: null,
    modelValue: {
      availability: {} as IAvailabilityData,
      availability_type: 0,
    },
  })
  state.modelValue = toValue(model.value)

  const callout = {
    title: globalT('availability_callout.title'),
    description: globalT('availability_callout.description'),
  }

  state.availabilities = state.availabilities.map((obj) => ({
    ...obj,
    label: obj.name,
    value: obj.id,
  }))

  state.allSalesTypes = [
    {
      id: 0,
      name: globalT('all_sales_type'),
      is_active: true,
      icon: 'sales-type',
      availability_id: 1,
    },
  ]

  state.salesTypeToggle = state.modelValue.availability_type === 0
  if (state.salesTypeToggle) {
    state.tableRows = generateTableRows(state.allSalesTypes)
  } else state.tableRows = generateTableRows(state.salesTypes)

  state.tableCols = [
    {
      label: globalT('sales_type'),
      align: 'left',
      field: 'salesType',
      name: 'salesType',
      headerClasses: 'q-px-sm',
      labelClasses: 'fs-13px fw-500 text-cool-gray-100',
    },
    {
      label: t('table_header.availability_sets'),
      align: 'left',
      field: 'availability',
      name: 'availability',
      style: 'width: 50%',
      headerClasses: 'q-px-sm',
      labelClasses: 'fs-13px fw-500 text-cool-gray-100',
    },
  ]

  /* -------------------------------- Functions ------------------------------- */

  /** Transforms the provided data to table row structure to be passed to the table component */
  function generateTableRows(salesTypes: ILocalSalesType[]) {
    return _.flatMap(salesTypes, (salesType) => {
      const existingAvailability = state.availabilityValues[salesType.id.toString()]
      const row: IBaseAvailabilityTableRow = {
        salesType: salesType.name,
        salesTypeId: salesType.id,
        icon: salesType.icon,
        active: salesType.id === 0 ? true : !existingAvailability ? salesType.is_active : existingAvailability.active,
        availabilityId: existingAvailability?.av_id ? existingAvailability.av_id : (salesType.availability_id ?? 0),
        salesTypeAvailabilityId: 0,
      }
      row.salesTypeAvailabilityId = row.availabilityId
      const availability = state.availabilities.find((a) => a.id === row.availabilityId)
      if (!availability) row.availabilityId = 1

      return row
    })
  }

  /** Transforms the table row data structure to the output format */
  function generateOutput(tableRows: IBaseAvailabilityTableRow[]) {
    if (tableRows.length === 0) return

    const salesTypesGroup = _.groupBy(tableRows, 'salesTypeId')
    let availabilityModel = _.cloneDeep(state.modelValue.availability)

    for (const salesTypeId of Object.keys(salesTypesGroup)) {
      const row = salesTypesGroup[salesTypeId][0]
      const salesTypeObj =
        salesTypeId === '0' ? state.allSalesTypes[0] : state.salesTypes.find((s) => s.id === Number(salesTypeId))

      if (salesTypeId === '0') {
        availabilityModel = {
          0: {
            active: true,
            av_id: row.availabilityId,
          },
        }
        break
      } else delete availabilityModel['0']

      if (row.active === salesTypeObj?.is_active && row.availabilityId === salesTypeObj?.availability_id) {
        availabilityModel[salesTypeId] = null
      } else {
        availabilityModel[salesTypeId] = { active: row.active }

        const model = availabilityModel[salesTypeId] ?? ({} as { active: boolean; av_id?: number })
        if ('active' in model) {
          if (row.active) model.av_id = row.availabilityId
          else delete model.av_id
        }
      }
    }

    state.modelValue.availability = _.omitBy(availabilityModel, _.isNull)
    state.modelValue.availability_type = state.salesTypeToggle ? 0 : 1
    model.value = state.modelValue
  }

  function showAvailabilityDetails(availabilityId: number) {
    state.availabilityDetails = state.availabilities.find((availability) => availability.id === availabilityId) ?? null
    state.availabilityDialog = true
  }

  /* -------------------------------- Watchers -------------------------------- */

  watch(
    () => state.salesTypeToggle,
    () => {
      if (state.salesTypeToggle) {
        state.tableRows = generateTableRows(state.allSalesTypes)
      } else state.tableRows = generateTableRows(state.salesTypes)
      generateOutput(state.tableRows)
    },
  )

  watch(
    () => state.tableRows,
    () => {
      generateOutput(state.tableRows)
    },
    { deep: true },
  )

  return { state, callout, t, globalT, showAvailabilityDetails }
}
